import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, Crown, Flame, Sparkles, HandHeart, ShoppingBag, PlayCircle, Quote, Home as HomeIcon } from "lucide-react";
import { api, BRAND } from "../lib/api";

export default function Home() {
  const [tts, setTts] = useState([]);
  useEffect(() => {
    api.get("/testimonials").then(r => setTts(r.data || [])).catch(()=>{});
  }, []);

  return (
    <div data-testid="page-home" className="">
      {/* HERO */}
      <section className="relative overflow-hidden pt-10 md:pt-16">
        <div className="absolute inset-0 hero-grid opacity-30" />
        {/* radiant glows */}
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full"
             style={{background:"radial-gradient(circle, rgba(217,22,104,0.18), transparent 65%)"}} />
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full"
             style={{background:"radial-gradient(circle, rgba(212,175,55,0.18), transparent 65%)"}} />

        <div className="container-narrow relative z-10 grid lg:grid-cols-12 gap-10 items-center pb-16 md:pb-24">
          {/* LEFT — Brand + tagline */}
          <div className="lg:col-span-7 reveal reveal-1">
            {/* Big crest */}
            <img
              src={BRAND.logoChurch}
              alt="The Surrender Church crest"
              className="h-32 w-32 md:h-44 md:w-44 rounded-full ring-4 ring-[#D4AF37]/60 shadow-2xl mb-8 object-cover"
            />

            <h1 className="font-serif leading-[0.92] tracking-tight text-white">
              <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-[96px]">
                The <span className="gold-text italic">Surrender</span>
              </span>
              <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-[96px]">
                Church
              </span>
            </h1>

            <div className="mt-6 inline-flex items-center gap-3 px-4 py-1.5 rounded-full bg-white/5 border border-[#D4AF37]/30">
              <Crown className="w-4 h-4 text-[#ECC23D]" />
              <span className="text-xs sm:text-sm tracking-[0.3em] uppercase font-semibold text-[#ECC23D]">
                Abundant Life II
              </span>
            </div>

            <p className="mt-7 font-serif italic text-2xl md:text-3xl text-white/85 max-w-xl leading-snug">
              Where <span className="gold-text">Surrender</span> meets <span className="fuchsia-text">Transformation</span>.
            </p>
            <p className="mt-4 text-base md:text-lg text-white/90 max-w-xl leading-relaxed">
              A movement led by {BRAND.pastor}. Break cycles. Gain clarity.
              Move forward with God — wherever you are in the world.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/services" data-testid="hero-join-service" className="btn-gold">
                <PlayCircle className="w-5 h-5" /> Join Service
              </Link>
              <Link to="/services#prayer" data-testid="hero-submit-prayer" className="btn-fuchsia">
                <HandHeart className="w-5 h-5" /> Submit Prayer
              </Link>
              <Link to="/booking" data-testid="hero-book-pastor" className="btn-ghost-gold">
                <Crown className="w-5 h-5" /> Book Pastor
              </Link>
              <Link to="/blueprint" data-testid="hero-shop-store" className="btn-ghost-gold !text-[#D91668] !border-[#D91668]/50 hover:!bg-[#D91668]/10">
                <ShoppingBag className="w-5 h-5" /> Shop Store
              </Link>
            </div>
          </div>

          {/* RIGHT — Pastor photo */}
          <div className="lg:col-span-5 reveal reveal-2 relative">
            <div className="relative max-w-md mx-auto lg:mx-0 lg:ml-auto">
              {/* Decorative gold corner accents */}
              <div className="absolute -top-4 -left-4 w-16 h-16 border-t-2 border-l-2 border-[#D4AF37] rounded-tl-3xl" />
              <div className="absolute -bottom-4 -right-4 w-16 h-16 border-b-2 border-r-2 border-[#D91668] rounded-br-3xl" />

              <div className="pastor-frame">
                <img
                  src={BRAND.homePhoto}
                  data-testid="home-hero-photo"
                  alt="Pastor Shalonda Raymond"
                  className="w-full h-auto block aspect-[4/5] object-cover"
                />
                {/* Soft bottom gradient with name overlay */}
                <div className="absolute inset-x-0 bottom-0 p-6"
                     style={{background:"linear-gradient(180deg, transparent 0%, rgba(11,18,48,0.85) 100%)"}}>
                  <div className="text-[10px] sm:text-xs tracking-[0.35em] uppercase font-semibold text-[#D4AF37]">Lead Pastor</div>
                  <div className="font-serif text-2xl sm:text-3xl text-white mt-1 leading-tight">Shalonda Raymond</div>
                </div>
              </div>

              {/* Gold sparkle badge */}
              <div className="absolute -top-3 -right-3 px-4 py-2 rounded-full bg-[#0000FF] shadow-xl border border-[#D4AF37]/60 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-xs font-semibold tracking-wider uppercase text-[#ECC23D]">Anointed</span>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee strip */}
        <div className="border-y border-[#D4AF37]/25 bg-white/5 py-5">
          <Marquee speed={40} gradient gradientColor="#0000FF" gradientWidth={120}>
            {Array(3).fill(0).map((_,i)=>(
              <div key={i} className="flex items-center gap-10 px-6 text-2xl md:text-3xl font-serif italic text-white/30">
                <span>Healing</span><span className="text-[#D4AF37]">·</span>
                <span>Growth</span><span className="text-[#D4AF37]">·</span>
                <span>Purpose</span><span className="text-[#D4AF37]">·</span>
                <span>Surrender</span><span className="text-[#D4AF37]">·</span>
                <span>Clarity</span><span className="text-[#D4AF37]">·</span>
              </div>
            ))}
          </Marquee>
        </div>
      </section>

      {/* VETERAN HOUSING BANNER */}
      <section className="container-narrow pt-12">
        <Link to="/services#veteran-housing" data-testid="home-veteran-banner"
          className="group flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-3xl border border-[#D4AF37]/50 bg-[#000099]/70 px-6 py-5 hover:border-[#D4AF37] transition-colors">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 shrink-0 rounded-2xl bg-[#D4AF37]/15 flex items-center justify-center"><HomeIcon className="w-6 h-6 text-[#ECC23D]" /></div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#F02078]">Now Accepting Applications · Limited Space</div>
              <div className="font-serif text-xl sm:text-2xl text-white">House of Bethea — Supportive Housing for Veterans</div>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#ECC23D] group-hover:gap-3 transition-all">Learn more & apply <ArrowRight className="w-4 h-4" /></span>
        </Link>
      </section>

      {/* WELCOME */}
      <section className="container-narrow py-24 md:py-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="label-luxe">A Word From Our Pastor</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
            Welcome home. <br/>
            <span className="fuchsia-text italic">Your next step starts here.</span>
          </h2>
          <p className="mt-6 text-white text-lg leading-relaxed">
            I'm Pastor Shalonda Raymond — and I built this place for the surrendered.
            For the ones who are tired of running, ready to lay it down, and hungry for a real
            encounter with the Holy Spirit.
          </p>
          <p className="mt-4 text-white/80 leading-relaxed">
            Whether you've been in the church your whole life or you're returning after a long
            season away, you are welcome at this altar. Pull up a chair. Let's break some cycles.
          </p>
          <Link to="/about" data-testid="welcome-learn-more" className="inline-flex items-center gap-2 mt-7 text-[#ECC23D] hover:text-[#D4AF37] font-semibold">
            Read Pastor Shalonda's story <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="lg:col-span-7 relative">
          <div className="relative rounded-[28px] overflow-hidden border border-white/10 shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1607390475878-9f924c2c7e0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxibGFjayUyMHdvbWFuJTIwd29yc2hpcCUyMHNpbGhvdWV0dGUlMjBjaW5lbWF0aWN8ZW58MHx8fHwxNzc3MTk3Mzg2fDA&ixlib=rb-4.1.0&q=85"
              alt="Surrendered worship"
              className="w-full h-[520px] object-cover"
            />
            <div className="absolute inset-0" style={{background:"linear-gradient(180deg, transparent 30%, rgba(11,18,48,0.85) 100%)"}} />
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
              <div className="font-serif italic text-2xl text-white max-w-md leading-tight" style={{textShadow:"0 2px 12px rgba(0,0,0,0.5)"}}>
                "The Word of God. The Fire of God. The River of Living Water."
              </div>
              <img src={BRAND.logoChurch} alt="crest" className="h-20 w-20 rounded-full ring-2 ring-[#D4AF37]/70" />
            </div>
          </div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="container-narrow pb-12">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: PlayCircle, title: "Online Services", text: "Sundays, Wednesdays & Friday Fire — gather virtually with the saints, in spirit and in truth.", to:"/services", color:"gold" },
            { icon: HandHeart, title: "Prayer Wall", text: "Submit your prayer privately or publicly. Pastor Shalonda and the intercessors will cover you.", to:"/services#prayer", color:"fuchsia" },
            { icon: Sparkles, title: "Next Step Blueprint™", text: "A faith-based system for clarity, alignment, and forward motion. Your breakthrough starts here.", to:"/blueprint", color:"gold" },
          ].map((p, i) => (
            <Link key={i} to={p.to} data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g,'-')}`} className="card-luxe block group">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-5 ${p.color==="gold" ? "bg-[#D4AF37]/15 text-[#ECC23D]" : "bg-[#D91668]/15 text-[#D91668]"}`}>
                <p.icon className="w-6 h-6" />
              </div>
              <h3 className="font-serif text-2xl mb-2">{p.title}</h3>
              <p className="text-white/90 leading-relaxed">{p.text}</p>
              <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#ECC23D] group-hover:gap-3 transition-all">
                Step in <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* NEXT STEP BLUEPRINT FEATURE — KEEP DARK FOR DRAMATIC CONTRAST */}
      <section className="container-narrow py-24">
        <div className="rounded-[32px] border border-[#D4AF37]/30 overflow-hidden relative card-dark">
          <div className="absolute inset-0 hero-grid opacity-40" />
          <div className="relative grid lg:grid-cols-2 gap-10 p-10 md:p-16 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37]">Featured Program · Healing Crowns</div>
              <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight text-white">
                The <span className="gold-text italic">Next Step</span><br/>
                <span className="fuchsia-text italic">Blueprint™</span>
              </h2>
              <p className="mt-5 text-white/90 text-lg leading-relaxed max-w-xl">
                A guided faith-based system for clarity, breakthrough, and forward movement.
                Break cycles. Gain direction. Move with purpose.
              </p>
              <ul className="mt-6 space-y-3 text-white/90">
                {["Clarity on your God-given assignment","Practical alignment for your daily walk","Action steps that produce real fruit"].map((b,i)=>(
                  <li key={i} className="flex items-start gap-3"><Crown className="w-5 h-5 text-[#D4AF37] mt-0.5"/>{b}</li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/blueprint" data-testid="blueprint-enroll" className="btn-fuchsia">Enroll Now <ArrowRight className="w-4 h-4"/></Link>
                <Link to="/blueprint" data-testid="blueprint-learn" className="btn-gold">Learn More</Link>
              </div>
            </div>
            <div className="relative">
              <img src={BRAND.logoBlueprint} alt="Next Step Blueprint" className="w-full max-w-md mx-auto rounded-3xl shadow-2xl"/>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="container-narrow py-20">
        <div className="text-center mb-14">
          <div className="label-luxe">Transformation Stories</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-3">When you surrender, things <span className="gold-text italic">shift</span>.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {(tts.length ? tts : [{quote:"Loading…", author:"", location:""}]).slice(0,3).map((t,i)=>(
            <div key={i} className="card-luxe">
              <Quote className="w-7 h-7 text-[#D4AF37]" />
              <p className="mt-4 text-white italic leading-relaxed">"{t.quote}"</p>
              <div className="mt-6 pt-5 border-t border-white/10">
                <div className="font-serif text-lg">{t.author}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/80">{t.location}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="container-narrow pb-24">
        <div className="rounded-3xl card-dark p-10 md:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 hero-grid opacity-20" />
          <Flame className="w-10 h-10 text-[#F02078] mx-auto relative z-10" />
          <h2 className="font-serif text-3xl md:text-5xl mt-4 max-w-3xl mx-auto leading-tight text-white relative z-10">
            Break cycles. Gain clarity. <span className="fuchsia-text italic">Move forward with God.</span>
          </h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 relative z-10">
            <Link to="/services" data-testid="cta-join-live" className="btn-gold">Join Us Live</Link>
            <Link to="/services#prayer" data-testid="cta-submit-prayer" className="btn-fuchsia">Submit a Prayer Request</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
